// Utils

import { Set as ImmutableSet } from 'immutable';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import type { Filter, FilterProps } from './filtersTypes';
import { type FilterObject, mkFilterObject } from './filtersTypes';

// web filter returns only an id which is the id of a category or a string name that needs to be mapped to an id
// also a type needs to be added to it
const categoryIdAndType = (catID: string) => {
  switch (catID) {
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
      return { id: '0', type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED };
    case 'BALANCE_ADJUSTMENT_0':
      return { id: '0', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_1':
      return { id: '1', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_2':
      return { id: '2', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_3':
      return { id: '3', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case '24':
      return { id: '24', type: chartOfAccountsTypes.CoaTypeEnum.ACCOUNT };
    default:
      return { id: catID, type: chartOfAccountsTypes.CoaTypeEnum.CATEGORY };
  }
};

// note - there is a potential loss of info here with storing start and end dates
export const convertFilterObjectToQCSFilter = (filter: FilterObject | null) => {
  if (!filter || !filter.numFilterItems) return null;

  const qcsFilter: FilterProps = { filterItems: [], type: 'REPORT' };
  if (filter?.categories?.size) {
    const itemArr = filter.categories.toArray().map((cat) => categoryIdAndType(cat));
    qcsFilter?.filterItems?.push({ chartOfAccounts: itemArr, type: 'CHARTOFACCOUNT' });
  }
  if (filter?.accounts?.size) {
    const itemArr = filter.accounts.toArray().map((acc) => ({ id: acc }));
    qcsFilter?.filterItems?.push({ accounts: itemArr, type: 'ACCOUNT' });
  }
  if (filter?.tags?.size) {
    const itemArr = filter.tags.toArray().map((tag) => ({ id: tag }));
    qcsFilter?.filterItems?.push({ tags: itemArr, type: 'TAG' });
  }
  if (filter?.payeeNames?.size) {
    const itemArr = filter.payeeNames.toArray().map((payee) => payee);
    qcsFilter?.filterItems?.push({ payees: itemArr, type: 'PAYEE' });
  }
  if (filter?.businesses?.size) {
    const itemArr = filter.businesses.toArray().map((business) => ({
      type: business === 'PERSONAL' ? 'PERSONAL' : 'BUSINESS',
      id: business === 'PERSONAL' ? '0' : business,
    }));
    qcsFilter?.filterItems?.push({ usages: itemArr, type: 'USAGE' });
  }
  return qcsFilter;
};

export const convertQCSFilterToFilterObject = (filter?: Filter) => {
  const retFilterObj = mkFilterObject();
  let numItems = 0;

  if (!filter) {
    return retFilterObj;
  }
  return retFilterObj.withMutations((mutable) => {
    filter?.filterItems?.forEach((item) => {
      switch (item.type) {
        case 'USAGE':
          if (item.usages) {
            mutable.set(
              'businesses',
              ImmutableSet(item.usages.map((it) => (it.type === 'PERSONAL' ? 'PERSONAL' : it.id))),
            );
            numItems += item.usages.length;
          }
          break;
        case 'ACCOUNT':
          if (item.accounts) {
            mutable.set('accounts', ImmutableSet(item.accounts.map((it) => it.id)));
            numItems += item.accounts.length;
          }
          break;
        case 'CHARTOFACCOUNT':
          if (item.chartOfAccounts) {
            mutable.set('categories', ImmutableSet(item.chartOfAccounts.map((it) => it.id)));
            numItems += item.chartOfAccounts.length;
          }
          break;
        case 'TAG':
          if (item.tags) {
            mutable.set('tags', ImmutableSet(item.tags.map((it) => it.id)));
            numItems += item.tags.length;
          }
          break;
        case 'PAYEE':
          if (item.payees) {
            mutable.set('payeeNames', ImmutableSet(item.payees));
            numItems += item.payees.length;
          }
          break;
        default:
          break;
      }
    });
    mutable.set('numFilterItems', numItems);
  });
};
